import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export const CARD_WEIGHT_OPTIONS = [
    { label: '250g', value: 0.25 },
    { label: '500g', value: 0.5 },
    { label: '1kg', value: 1 },
];

/** Robust check for admin flag "Activer les Variantes de Poids" */
export const isProductHasVariants = (productOrFlag) => {
    if (productOrFlag && typeof productOrFlag === 'object') {
        const value = productOrFlag.has_variants;
        return value === true || value === 1 || value === '1' || value === 'true';
    }
    return productOrFlag === true || productOrFlag === 1 || productOrFlag === '1' || productOrFlag === 'true';
};

export const getDefaultCardSelection = (hasVariants = false) => ({
    quantity: 1,
    weightLabel: null,
    weightValue: null,
    hasVariants: Boolean(hasVariants),
    counterExpanded: false,
});

/**
 * Compact +/- control for Accueil product cards.
 * If has_variants: + opens 250g/500g/1kg locally (no product page load).
 */
const ProductCardQuantity = ({
    hasVariants = false,
    selection,
    onChange,
    className = '',
    collapsedUntilInteraction = false,
}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const rootRef = useRef(null);
    const menuRef = useRef(null);

    const variantsEnabled = isProductHasVariants(hasVariants);
    const current = selection || getDefaultCardSelection(variantsEnabled);
    const quantity = Math.max(1, current.quantity || 1);
    const weightLabel = current.weightLabel || null;
    const counterExpanded = Boolean(current.counterExpanded);
    const isExpanded = !collapsedUntilInteraction
        || counterExpanded
        || Boolean(weightLabel)
        || quantity > 1;

    const updateMenuPosition = () => {
        if (!rootRef.current) return;
        const rect = rootRef.current.getBoundingClientRect();
        const menuWidth = 104;
        const left = Math.min(
            Math.max(8, rect.right - menuWidth),
            window.innerWidth - menuWidth - 8
        );
        setMenuPos({
            top: rect.bottom + 6 + window.scrollY,
            left: left + window.scrollX,
        });
    };

    useLayoutEffect(() => {
        if (!menuOpen) return undefined;
        updateMenuPosition();
        const onScrollOrResize = () => updateMenuPosition();
        window.addEventListener('resize', onScrollOrResize);
        window.addEventListener('scroll', onScrollOrResize, true);
        return () => {
            window.removeEventListener('resize', onScrollOrResize);
            window.removeEventListener('scroll', onScrollOrResize, true);
        };
    }, [menuOpen]);

    useEffect(() => {
        if (!menuOpen) return undefined;

        const handleOutside = (event) => {
            const inRoot = rootRef.current && rootRef.current.contains(event.target);
            const inMenu = menuRef.current && menuRef.current.contains(event.target);
            if (!inRoot && !inMenu) {
                setMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [menuOpen]);

    const emit = (next) => {
        onChange?.({
            quantity: next.quantity ?? quantity,
            weightLabel: Object.prototype.hasOwnProperty.call(next, 'weightLabel')
                ? next.weightLabel
                : weightLabel,
            weightValue: Object.prototype.hasOwnProperty.call(next, 'weightValue')
                ? next.weightValue
                : current.weightValue,
            hasVariants: variantsEnabled,
            counterExpanded: Object.prototype.hasOwnProperty.call(next, 'counterExpanded')
                ? next.counterExpanded
                : counterExpanded,
        });
    };

    const stopAll = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const collapseIfNeeded = () => {
        if (collapsedUntilInteraction && quantity <= 1 && !weightLabel) {
            emit({ counterExpanded: false });
        }
    };

    const handleMinus = (e) => {
        stopAll(e);

        if (variantsEnabled) {
            if (weightLabel) {
                emit({ weightLabel: null, weightValue: null, quantity: 1 });
            }
            collapseIfNeeded();
            setMenuOpen(false);
            return;
        }

        if (quantity > 1) {
            emit({ quantity: quantity - 1 });
            return;
        }

        collapseIfNeeded();
    };

    const handlePlus = (e) => {
        stopAll(e);

        if (collapsedUntilInteraction && !isExpanded) {
            emit({ counterExpanded: true });
            return;
        }

        if (variantsEnabled) {
            setMenuOpen((open) => {
                const next = !open;
                if (next) {
                    // Position will refresh in layout effect
                    requestAnimationFrame(updateMenuPosition);
                }
                return next;
            });
            return;
        }

        emit({ quantity: quantity + 1 });
    };

    const handleSelectWeight = (e, option) => {
        stopAll(e);
        emit({
            quantity: 1,
            weightLabel: option.label,
            weightValue: option.value,
        });
        setMenuOpen(false);
    };

    const displayValue = variantsEnabled
        ? (weightLabel || 'Poids')
        : String(quantity);

    const btnClass =
        'flex-center border border-gray-100 rounded-4 hover-bg-main-600 hover-text-white flex-shrink-0';

    const counterShellClass =
        'd-flex align-items-center justify-content-center gap-4 border border-gray-100 rounded-8 bg-white px-4 py-2 shadow-sm';

    const collapsedAddBtnClass =
        'product-card-qty__add flex-center border border-gray-100 rounded-8 bg-white text-heading flex-shrink-0 transition-1 hover-bg-main-600 hover-text-white hover-border-main-600';

    const weightMenu = menuOpen && variantsEnabled
        ? createPortal(
            <div
                ref={menuRef}
                className="bg-white border border-gray-100 rounded-8 shadow-sm"
                style={{
                    position: 'absolute',
                    zIndex: 10050,
                    top: menuPos.top,
                    left: menuPos.left,
                    minWidth: 104,
                    padding: 6,
                }}
                onMouseDown={stopAll}
                onClick={stopAll}
            >
                {CARD_WEIGHT_OPTIONS.map((option) => (
                    <button
                        key={option.label}
                        type="button"
                        onMouseDown={(e) => handleSelectWeight(e, option)}
                        onClick={(e) => handleSelectWeight(e, option)}
                        className={`w-100 border-0 rounded-4 fw-semibold py-6 px-8 mb-2 text-center ${
                            weightLabel === option.label
                                ? 'bg-main-600 text-white'
                                : 'bg-color-three text-heading hover-bg-main-50'
                        }`}
                        style={{ cursor: 'pointer', fontSize: 12 }}
                    >
                        {option.label}
                    </button>
                ))}
            </div>,
            document.body
        )
        : null;

    return (
        <div
            ref={rootRef}
            className={`product-card-qty position-relative d-inline-flex flex-column align-items-center flex-shrink-0 ${className}`}
            onClick={stopAll}
            onMouseDown={stopAll}
        >
            {collapsedUntilInteraction && !isExpanded ? (
                <button
                    type="button"
                    onClick={handlePlus}
                    onMouseDown={stopAll}
                    className={collapsedAddBtnClass}
                    style={{
                        width: 36,
                        height: 36,
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    }}
                    aria-label={variantsEnabled ? 'Choisir la quantité ou le poids' : 'Afficher le compteur'}
                >
                    <i className="ph ph-plus" style={{ fontSize: 16, lineHeight: 1 }} />
                </button>
            ) : (
                <div className={counterShellClass}>
                    <button
                        type="button"
                        onClick={handleMinus}
                        onMouseDown={stopAll}
                        className={btnClass}
                        style={{ width: 24, height: 24, cursor: 'pointer', background: 'transparent' }}
                        aria-label="Diminuer"
                    >
                        <i className="ph ph-minus" style={{ fontSize: 12 }} />
                    </button>
                    <span
                        className="text-center fw-semibold text-heading"
                        style={{
                            minWidth: variantsEnabled ? 36 : 18,
                            fontSize: 12,
                            lineHeight: 1.2,
                        }}
                    >
                        {displayValue}
                    </span>
                    <button
                        type="button"
                        onClick={handlePlus}
                        onMouseDown={stopAll}
                        className={btnClass}
                        style={{ width: 24, height: 24, cursor: 'pointer', background: 'transparent' }}
                        aria-label={variantsEnabled ? 'Choisir le poids' : 'Augmenter'}
                        title={variantsEnabled ? 'Choisir 250g / 500g / 1kg' : undefined}
                    >
                        <i className="ph ph-plus" style={{ fontSize: 12 }} />
                    </button>
                </div>
            )}
            {weightMenu}
        </div>
    );
};

export default ProductCardQuantity;
